// src/pages/ChatPage.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, MapPin, Calendar, RefreshCw } from "lucide-react";
import { TopBar, LoadingDots, RoadBadge, ErrorMsg } from "../components/UI";
import { api } from "../services/api";

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} animate-fade-up`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-vine/10 border border-vine/20
                        flex items-center justify-center mr-2 mt-1 flex-shrink-0">
          <span className="text-vine text-xs font-display font-bold">W</span>
        </div>
      )}
      <div className={isUser ? "bubble-user" : "bubble-ai"}>
        {msg.content}
      </div>
    </div>
  );
}

function TourCard({ tour }) {
  if (!tour) return null;
  return (
    <div className="card border-oak/30 bg-oak/5 animate-fade-up">
      <div className="flex items-center gap-2 mb-3">
        <RoadBadge slug={tour.road_slug} />
        <span className="text-xs text-stone flex items-center gap-1">
          <Calendar size={11} /> {tour.days} days
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {tour.stops?.map((stop, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="w-5 h-5 rounded-full bg-vine/10 text-vine
                            text-xs font-medium flex items-center justify-center
                            flex-shrink-0 mt-0.5">
              {i + 1}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-dark">{stop.winery_name}</p>
              <p className="text-xs text-stone flex items-center gap-1 mt-0.5">
                <MapPin size={10} /> Day {stop.day} · {stop.slot_time}
              </p>
            </div>
          </div>
        ))}
      </div>
      <button className="btn-primary w-full mt-4">
        Save this tour
      </button>
    </div>
  );
}

export default function ChatPage() {
  const [searchParams] = useSearchParams(); // kept for backward compat
  const navigate       = useNavigate();

  const _stored = (() => { try { const s = sessionStorage.getItem("chat_init"); sessionStorage.removeItem("chat_init"); return s ? JSON.parse(s) : null; } catch { return null; } })();
  const sessionId  = _stored?.sessionId || searchParams.get("session") || `session-${Date.now()}`;
  const initMsg    = _stored?.message || searchParams.get("init");
  const roadSlug   = _stored?.road || searchParams.get("road");

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your wine route guide. Tell me about your perfect tour — or use the form to get started and I'll take it from there.",
    },
  ]);
  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [tour,    setTour]    = useState(null);

  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const initSent   = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Auto-send the init message from the plan form
  useEffect(() => {
    if (initMsg && !initSent.current) {
      initSent.current = true;
      sendMessage(decodeURIComponent(initMsg));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = useCallback(async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;

    setInput("");
    setError("");
    setMessages(m => [...m, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const convHistory = messages.map(m => m.role !== "tour" ? {role: m.role, content: m.content} : null).filter(Boolean);
      const res = await api.chat(sessionId, userMsg, convHistory);

      setMessages(m => [...m, {
        role: "assistant",
        content: res.reply,
      }]);

      if (res.tour) {
        setTour(res.tour);
      }
    } catch (err) {
      setError(err.message);
      setMessages(m => [...m, {
        role: "assistant",
        content: "Something went wrong. Please try again.",
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, sessionId]);

  function handleSubmit(e) {
    e.preventDefault();
    sendMessage();
  }

  const QUICK_REPLIES = [
    "Can we add more wineries?",
    "Make it more budget-friendly",
    "Focus on organic producers",
    "Show me the best rated stops",
  ];

  return (
    <div className="flex flex-col" style={{height:"calc(100vh - 56px)",background:"#F2F5EC"}}>
      <TopBar
        title={roadSlug ? `Tour — ${roadSlug.replace(/-/g, " ")}` : "Tour chat"}
        back={() => navigate("/plan")}
        right={
          <button onClick={() => navigate("/plan")}
            className="btn-ghost text-xs gap-1 py-1.5 px-2">
            <RefreshCw size={13} /> New
          </button>
        }
      />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 pb-2">
        {messages.map((msg, i) => <Message key={i} msg={msg} />)}

        {loading && (
          <div className="flex justify-start animate-fade-up">
            <div className="w-7 h-7 rounded-full bg-vine/10 border border-vine/20
                            flex items-center justify-center mr-2 flex-shrink-0">
              <span className="text-vine text-xs font-display font-bold">W</span>
            </div>
            <div className="bubble-ai">
              <LoadingDots />
            </div>
          </div>
        )}

        {tour && <TourCard tour={tour} />}

        {error && <ErrorMsg message={error} />}

        <div ref={bottomRef} />
      </div>

      {/* Quick replies */}
      {!loading && messages.length < 4 && (
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-none">
          {QUICK_REPLIES.map(r => (
            <button key={r}
              onClick={() => sendMessage(r)}
              className="flex-shrink-0 text-xs border border-vine/30 text-vine
                         bg-vine/5 rounded-full px-3 py-1.5 hover:bg-vine/10
                         transition-colors whitespace-nowrap">
              {r}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{borderTop:"1px solid #222",background:"#E8EDE0",padding:"10px 16px"}} style={{background:"#E8EDE0",borderColor:"#C8D4B0",paddingBottom:"calc(env(safe-area-inset-bottom) + 70px)"}}>
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about wines, routes, anything…"
            rows={1}
            className="input flex-1 resize-none leading-5 min-h-[44px] max-h-32
                       py-2.5 overflow-auto"
            style={{ height: "auto" }}
          />
          <button type="submit"
            disabled={loading || !input.trim()}
            className="btn-primary h-11 w-11 p-0 flex-shrink-0">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
